import React, { useContext, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Context as AuthContext } from '../../contexts/AuthContext'
import { useUsers, useHistory, useIsDesktop } from '../../hooks'
import { imageUrls } from '../../constants'
import { PageHeader, FullPageLoader } from '../../components'
import { memberMetaText, hideBlockedReason } from '../../utilities/memberViewHelpers'
import styles from './GroupMembersPage.module.scss'

const FALLBACK_AVATAR = 'https://storage.googleapis.com/baby-dragon/public/dummy_user_L.png'

const GroupMembersPage = () => {
  const { state: authState } = useContext(AuthContext)
  const { group_id } = useParams()
  const history = useHistory()
  const isDesktop = useIsDesktop()
  const [users, loading, , getUsers] = useUsers(authState, { includeHidden: true })
  const [busyId, setBusyId] = useState(null)
  const [openId, setOpenId] = useState(null)

  const myId = authState.userLineIdToken
  const activeMembers = users.filter(u => !u.hidden)
  const hiddenMembers = users.filter(u => u.hidden)

  const toggle = (id) => setOpenId(openId === id ? null : id)

  const runAction = (user, request) => {
    if (busyId) { return }
    setBusyId(user.id)
    request(group_id, user.id)
      .then(() => getUsers())
      .catch(() => getUsers()) // re-sync eligibility if the server rejected
      .finally(() => setBusyId(null))
  }

  const hide = (user) => runAction(user, (gid, uid) => authState.api.hideUser(gid, uid))
  const restore = (user) => runAction(user, (gid, uid) => authState.api.restoreUser(gid, uid))

  const goToBook = (book) => {
    history.navigate(
      `/liff_entry/groups/${group_id}/accounting_books/${book.id}/overview`,
      { backLink: `/liff_entry/groups/${group_id}/members` }
    )
  }

  const renderBooks = (user) => {
    if (!user.accountingBooks || user.accountingBooks.length === 0) {
      return <div className={styles.noRecord}>這位成員沒有任何帳款紀錄。</div>
    }

    return user.accountingBooks.map(book => (
      <div className={styles.bookItem} key={book.id} onClick={() => goToBook(book)}>
        <img className={styles.bookIcon} src={imageUrls[book.imageId || 0]} alt="" />
        <span className={styles.bookName}>{book.name}</span>
        <span className={[styles.status, book.settled ? styles.settled : styles.unsettled].join(' ')}>
          {book.settled ? '已結清' : '未結清'}
        </span>
        <span className={styles.chevSmall}>›</span>
      </div>
    ))
  }

  const renderActions = (user, section) => {
    const isBusy = busyId === user.id

    if (section === 'hidden') {
      return (
        <button
          className={[styles.btn, styles.restoreBtn].join(' ')}
          disabled={isBusy}
          onClick={() => restore(user)}>
          恢復顯示
        </button>
      )
    }

    return (
      <>
        <button
          className={[styles.btn, styles.hideBtn].join(' ')}
          disabled={!user.hideable || isBusy}
          onClick={() => hide(user)}>
          隱藏
        </button>
        { !user.hideable && <div className={styles.hint}>{hideBlockedReason(user)}</div> }
      </>
    )
  }

  const renderMember = (user, section) => {
    const isOpen = openId === user.id
    return (
      <div
        className={[styles.member, isOpen ? styles.open : '', user.hidden ? styles.hiddenMember : ''].join(' ')}
        key={user.id}>
        <div className={styles.row} onClick={() => toggle(user.id)}>
          <img className={styles.avatar} src={user.imageURL || FALLBACK_AVATAR} alt="" />
          <div className={styles.info}>
            <div className={styles.name}>
              <span className={styles.displayName}>{user.displayName}</span>
              {
                user.fromLine
                  ? <span className={[styles.tag, styles.line].join(' ')}>LINE 用戶</span>
                  : <span className={[styles.tag, styles.virtual].join(' ')}>虛擬成員</span>
              }
              { user.id === myId && <span className={[styles.tag, styles.self].join(' ')}>你</span> }
            </div>
            <div className={styles.meta}>{memberMetaText(user)}</div>
          </div>
          <span className={styles.chev}>›</span>
        </div>

        {
          isOpen &&
            <div className={styles.detail}>
              { renderBooks(user) }
              <div className={styles.actions}>{ renderActions(user, section) }</div>
            </div>
        }
      </div>
    )
  }

  return (
    <div className={styles.bg}>
      { !isDesktop &&
        <PageHeader
          faicon="faChevronLeft"
          link={`/liff_entry/groups/${group_id}/accounting_books`}>
          群組成員
        </PageHeader>
      }

      {
        loading
          ? <FullPageLoader />
          : (
            <div className={styles.body}>
              <p className={styles.pageSub}>這個群組共有 <b>{users.length}</b> 位成員 · 點擊成員可以查看帳本紀錄</p>

              <div className={styles.sectionLabel}>
                <span>使用中</span>
                <span className={styles.count}>{activeMembers.length}</span>
              </div>
              <div className={styles.list}>
                { activeMembers.map(u => renderMember(u, 'active')) }
              </div>

              <div className={styles.sectionLabel}>
                <span>已隱藏</span>
                <span className={styles.count}>{hiddenMembers.length}</span>
              </div>
              {
                hiddenMembers.length === 0
                  ? <p className={styles.emptyHidden}>被隱藏的成員不會出現在新帳本的成員名單，舊帳款紀錄仍然保留。</p>
                  : <div className={styles.list}>{ hiddenMembers.map(u => renderMember(u, 'hidden')) }</div>
              }
            </div>
          )
      }
    </div>
  )
}

export default GroupMembersPage
